import { memo, useContext, useMemo } from "react";

import { FieldArray, ErrorMessage, useFormikContext } from "formik";
import { findBy, isNotPresent, isPresent } from "neetocist";
import { Plus, Delete } from "neetoicons";
import { Button } from "neetoui";
import { Select } from "neetoui/formik";
import { useTranslation } from "react-i18next";

import {
  REMOVE_SELECT_DOWN_ARROW,
  BLUR_TEXT_WHEN_SELECT_MENU_IS_OPEN,
} from "./constants";
import {
  filterSlots,
  generateNewPeriod,
  addDefaultPeriod,
  isValidTimeSlotOption,
  formatCreateLabel,
  buildSlotIntervals,
  formatTimeSlotOption,
} from "./utils";

import ScheduleContext from "../ScheduleContext";

const ScheduleRow = ({ day }) => {
  const { values, setFieldValue } = useFormikContext();
  const bookedSlots = values.wdays[day].periods;

  const { allowAddPeriod, slotInterval, hourFormat } =
    useContext(ScheduleContext);

  const { t } = useTranslation();

  const sortSlotsInterval = useMemo(
    () =>
      buildSlotIntervals({ interval: slotInterval, bookedSlots, hourFormat }),
    [slotInterval, bookedSlots, hourFormat]
  );

  const startTimeOptions = period =>
    sortSlotsInterval("startTime", period?.startTime);

  const endTimeOptions = period =>
    sortSlotsInterval("endTime", period?.endTime);

  const handleChange = (index, field, e) => {
    const value = formatTimeSlotOption(e?.value);
    setFieldValue(`wdays.${day}.periods[${index}].${field}`, value);
  };

  const handleAddRow = arrayHelpers => {
    if (isNotPresent(values.wdays[day].periods)) {
      arrayHelpers.push(addDefaultPeriod(day));
    } else {
      arrayHelpers.push(generateNewPeriod(values.wdays[day].periods, day));
    }
    setFieldValue(`wdays.${day}.available`, true);
  };

  const handleDeleteRow = (arrayHelpers, index) => {
    const isLastRowDeleted = values.wdays[day].periods.length === 1;
    arrayHelpers.remove(index);
    setFieldValue(`wdays.${day}.available`, !isLastRowDeleted);
  };

  return (
    <FieldArray
      name={`wdays.${day}.periods`}
      render={arrayHelpers => (
        <>
          {isPresent(values.wdays[day]?.periods) && (
            <div className="flex w-full flex-col gap-y-2">
              {values.wdays[day].periods?.map((period, index) => (
                <div
                  className="flex w-full flex-col @xl:mx-auto @xl:w-80"
                  key={`wdays.${day}.periods[${index}]`}
                >
                  <div
                    className="flex items-start gap-2 @xl:gap-3"
                    data-testid="schedule-row"
                  >
                    <Select
                      {...{ formatCreateLabel }}
                      isClearable
                      isCreateable
                      isSearchable
                      className="w-full"
                      components={REMOVE_SELECT_DOWN_ARROW}
                      filterOption={filterSlots}
                      isValidNewOption={isValidTimeSlotOption}
                      name={`wdays.${day}.periods[${index}].startTime`}
                      options={startTimeOptions(period)}
                      placeholder={t("neetoMolecules.schedule.startTime")}
                      strategy="fixed"
                      styles={BLUR_TEXT_WHEN_SELECT_MENU_IS_OPEN}
                      value={findBy(
                        { value: period?.startTime },
                        startTimeOptions(period)
                      )}
                      onChange={e => handleChange(index, "startTime", e)}
                    />
                    <Select
                      {...{ formatCreateLabel }}
                      isClearable
                      isCreateable
                      isSearchable
                      className="w-full"
                      components={REMOVE_SELECT_DOWN_ARROW}
                      filterOption={filterSlots}
                      isValidNewOption={isValidTimeSlotOption}
                      name={`wdays.${day}.periods[${index}].endTime`}
                      options={endTimeOptions(period)}
                      placeholder={t("neetoMolecules.schedule.endTime")}
                      strategy="fixed"
                      styles={BLUR_TEXT_WHEN_SELECT_MENU_IS_OPEN}
                      value={findBy(
                        { value: period?.endTime },
                        endTimeOptions(period)
                      )}
                      onChange={e => handleChange(index, "endTime", e)}
                    />
                    <Button
                      className="flex-shrink-0 self-start"
                      data-testid="delete-period-button"
                      icon={Delete}
                      style="text"
                      onClick={() => handleDeleteRow(arrayHelpers, index)}
                    />
                  </div>
                  <ErrorMessage name={`wdays.${day}.periods[${index}]`}>
                    {message =>
                      typeof message !== "object" && (
                        <span className="neeto-ui-text-error-500 mt-1 w-11/12 px-2 text-xs">
                          {message}
                        </span>
                      )
                    }
                  </ErrorMessage>
                </div>
              ))}
            </div>
          )}
          {values.wdays[day].available && allowAddPeriod && (
            <Button
              className="absolute end-14 top-3 ms-auto flex-shrink-0 @xl:static"
              data-testid="add-new-period-button"
              icon={Plus}
              style="secondary"
              tooltipProps={{
                content: t("neetoMolecules.schedule.addNewPeriod"),
                position: "top",
                hideAfter: 5000,
                touch: ["hold", 500],
              }}
              onClick={() => handleAddRow(arrayHelpers)}
            />
          )}
        </>
      )}
    />
  );
};

export default memo(ScheduleRow);
